import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 25000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("packo_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fmt = (n) => {
  const num = Number(n || 0);
  const hasDecimals = Math.round(num * 100) % 100 !== 0;
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: 2 })}`;
};

const UNIT_NAMES = { pc: "pc", pack: "pack", box: "box", roll: "roll", set: "set", kg: "kg", g: "g", L: "L", m: "m" };
const UNIT_PLURALS = { pc: "pcs", pack: "packs", box: "boxes", roll: "rolls", set: "sets" };

export const unitName = (u) => UNIT_NAMES[u] || u || "pc";
export const pluralUnit = (u, n = 2) => (n === 1 ? unitName(u) : UNIT_PLURALS[unitName(u)] || unitName(u));
export const fmtPerUnit = (n, u) => `${fmt(n)}/${unitName(u)}`;
export const fmtQty = (n, u) => `${Number(n || 0).toLocaleString("en-IN")} ${pluralUnit(u, n)}`;

export const fmtSigned = (n) => (Number(n) < 0 ? `−${fmt(Math.abs(Number(n)))}` : fmt(n));

// Invoice PDF download (auth via interceptor; guests pass their order access token).
export async function downloadInvoicePdf(orderId, access = "") {
  const res = await api.get(`/orders/${orderId}/invoice/pdf`, {
    params: access ? { access } : {},
    responseType: "blob",
    timeout: 60000,
  });
  const dispo = res.headers?.["content-disposition"] || "";
  const match = dispo.match(/filename="?([^";]+)"?/);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : "PAQVERO-Invoice.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Razorpay Checkout (client opens the modal; ONLY the server can mark an order paid) ----
let razorpayScriptPromise = null;
export function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => reject(new Error("Couldn't load the payment gateway"));
      document.body.appendChild(s);
    });
  }
  return razorpayScriptPromise;
}

export function openRazorpayCheckout({ keyId, orderId, amountPaise, prefill, description }) {
  return loadRazorpayScript().then(() => new Promise((resolve, reject) => {
    const teardown = () => {
      // Razorpay ignores close() while its own retry screen owns the modal — with
      // retry disabled it dismisses itself; this sweep removes any leftover nodes.
      try { rzp.close(); } catch { /* already closed */ }
      setTimeout(() => {
        document.querySelectorAll(".razorpay-container, iframe.razorpay-checkout-frame").forEach((n) => n.remove());
      }, 300);
    };
    const rzp = new window.Razorpay({
      key: keyId,
      order_id: orderId,
      amount: amountPaise,
      currency: "INR",
      name: "PAQVERO",
      description,
      prefill,
      theme: { color: "#FF9F1C" },
      retry: { enabled: false },
      handler: (resp) => { teardown(); resolve(resp); },
      modal: { ondismiss: () => { teardown(); reject(Object.assign(new Error("Payment cancelled"), { reason: "cancelled" })); } },
    });
    rzp.on("payment.failed", (resp) => {
      teardown();
      reject(Object.assign(new Error(resp?.error?.description || "Payment failed at the gateway"), { reason: "failed" }));
    });
    rzp.open();
  }));
}

// Session-scoped delivery PIN memory (never written to the customer account).
const SESSION_PIN_KEY = "packo_delivery_pin";
const SESSION_QUOTE_KEY = "packo_delivery_quote";

export const getSessionPin = () => sessionStorage.getItem(SESSION_PIN_KEY) || "";

export const getSessionQuote = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_QUOTE_KEY) || "null");
  } catch {
    return null;
  }
};

export const setSessionDelivery = (pin, quote) => {
  sessionStorage.setItem(SESSION_PIN_KEY, pin);
  sessionStorage.setItem(SESSION_QUOTE_KEY, JSON.stringify(quote));
};

export const clearSessionDelivery = () => {
  sessionStorage.removeItem(SESSION_PIN_KEY);
  sessionStorage.removeItem(SESSION_QUOTE_KEY);
};

export function apiError(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  }
  return fallback;
}

export const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export const fmtDateLong = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
